import { Alert, Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { settingsHref } from '@/features/settings/settingsHref';
import { DeletionPendingAlert } from './DeletionPendingAlert';
import { useMe } from './meHooks';

/** The renew path: Settings > Subscription (the locked screen and the locked banner). */
const BILLING_PATH = settingsHref('subscription');

function formatSince(iso: string | null, locale: string, timeZone: string): string {
  if (iso === null) return '';
  try {
    return formatInstant(iso, locale, timeZone);
  } catch {
    return iso;
  }
}

export function isUnlockedRoute(pathname: string): boolean {
  return pathname.startsWith('/me/settings') || pathname.startsWith('/auth/');
}

/**
 * The routes the locked state leaves open: Settings and `/auth/*`, and My Financial Assistant, where
 * a locked member sees only Help & feedback's tickets (list, view, file a support case, reply, close,
 * download; Operator, 2026-10-16, TMMAPP-0142 M1). The subscribe gate's own exemptions stay
 * `isUnlockedRoute`. The ticket emails' `/assistant` link (it redirects to `/me/assistant`, keeping
 * `?ticket=`) is open too, so a locked member reaches the ticket (TMMAPP-0148 E1): exactly
 * `/assistant`, never a path under it (TMMAPP-0153 item 11). A locked member reads help and
 * announcements there too (TMMAPI-0186 U1 item 7).
 */
export function isLockedExemptRoute(pathname: string): boolean {
  return isUnlockedRoute(pathname) || /^\/assistant$|^\/me\/assistant(\/|$)/.test(pathname);
}

/** The locked state's banner on a route it leaves open (Help & feedback), with the renew path. */
export function LockedBanner() {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const since = formatSince(me.lockedAt, locale, me.user.timezone ?? 'UTC');
  return (
    <Alert
      severity="warning"
      data-locked-banner=""
      action={
        <Button color="inherit" size="small" component={RouterLink} to={BILLING_PATH}>
          {t('locked.goToBilling')}
        </Button>
      }
    >
      {since.length > 0 ? t('locked.body', { since }) : t('locked.bodyNoSince')}
    </Alert>
  );
}

export function LockedScreen() {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const title = usePageTitle({ title: t('locked.title') });
  const since = formatSince(me.lockedAt, locale, me.user.timezone ?? 'UTC');
  return (
    <Box component="section">
      <PageHeader title={title} />
      <Typography variant="body1" sx={{ mb: 3 }}>
        {since.length > 0 ? t('locked.body', { since }) : t('locked.bodyNoSince')}
      </Typography>
      <Button variant="contained" component={RouterLink} to={BILLING_PATH}>
        {t('locked.goToBilling')}
      </Button>
      {me.deletionRequested !== null && (
        <Box sx={{ mt: 3 }}>
          <DeletionPendingAlert until={me.deletionRequested.coolingOffUntil} />
        </Box>
      )}
    </Box>
  );
}
